import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addPartnerOrg,
  clearPartnerBrandingSubdomain,
  createPartner,
  createPartnerUser,
  deletePartnerUser,
  downloadBillingReport,
  getPartnerBranding,
  getPartnerById,
  getPartnerEnabledJurisdictions,
  getPartnerPackaging,
  getPartners,
  getPartnerPortfolioApiKeys,
  getPartnerSettings,
  getPartnerUsers,
  getPartnersReport,
  mintPartnerPortfolioApiKey,
  removePartnerOrg,
  setPartnerBrandingSubdomain,
  revokePartnerPortfolioApiKey,
  updatePartner,
  updatePartnerEnabledJurisdictions,
  updatePartnerPackaging,
  updatePartnerSettings,
  updatePartnerUser,
  PARTNERS_BY_ID_STATE_KEY,
  PARTNERS_LIST_KEY,
  PARTNER_USERS_KEY,
  PARTNER_API_KEYS_KEY,
  PARTNER_BRANDING_KEY,
  PARTNER_ENABLED_JURISDICTIONS_KEY,
  PARTNER_PACKAGING_KEY,
  PARTNER_SETTINGS_KEY,
} from '../partners';
import { ORGANIZATIONS_STATE_KEY } from '../organizations-apis';
import {
  PartnerBrandingSubdomainUpdate,
  PartnerCreatePayload,
  PartnerEnabledJurisdictionsUpdate,
  PartnerFeatureSettingsUpdate,
  PartnerOrgCreatePayload,
  PartnerPackagingSettingsUpdate,
  PartnerUpdatePayload,
  PartnerUserCreatePayload,
  PartnerUserUpdatePayload,
} from '@/types/partners';
import { V2_ADMIN_PREFIX } from '../api';
import { downloadBlob, getFilenameFromHeaders, useDownloadReportMutation } from './reports';
import { toaster } from '@/components/ui/toaster-instance';

export const usePartnerByIdQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNERS_BY_ID_STATE_KEY, partnerId],
    queryFn: async () => {
      if (!partnerId) {
        return null;
      }
      const { data } = await getPartnerById(partnerId);
      return data;
    },
    enabled: !!partnerId,
  });
};

export const usePartnersQuery = (searchQuery?: string) => {
  return useQuery({
    queryKey: [PARTNERS_LIST_KEY, searchQuery ?? ''],
    queryFn: () => getPartners(searchQuery),
  });
};

export const useDownloadPartnersReport = () => {
  return useDownloadReportMutation(
    'Partner report',
    'partners-report',
    async () => {
      const response = await getPartnersReport();
      return response.data as Blob;
    },
    'csv'
  );
};

export const useCreatePartnerMutation = () => {
  return useMutation({
    mutationFn: (payload: PartnerCreatePayload) => createPartner(payload),
  });
};

export const useUpdatePartnerMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerUpdatePayload) => updatePartner(partnerId, payload),
    onSuccess: async updatedPartner => {
      queryClient.setQueryData([PARTNERS_BY_ID_STATE_KEY, partnerId], updatedPartner);
      await queryClient.invalidateQueries({
        queryKey: [PARTNERS_BY_ID_STATE_KEY, partnerId],
      });
      // Convert-to-live can change FEATURES timestamps and is_test on settings,
      // plus Test/Live on the managed-orgs table and the partners sidebar list.
      await queryClient.invalidateQueries({
        queryKey: [PARTNER_SETTINGS_KEY, partnerId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['data-table', `${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations`],
      });
      await queryClient.invalidateQueries({
        queryKey: [PARTNERS_LIST_KEY],
      });
    },
  });
};

export const usePartnerUsersQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNER_USERS_KEY, partnerId],
    queryFn: () => getPartnerUsers(partnerId),
    enabled: !!partnerId,
  });
};

export const useCreatePartnerUserMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerUserCreatePayload) => createPartnerUser(partnerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_KEY, partnerId] });
    },
  });
};

export const useUpdatePartnerUserMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: PartnerUserUpdatePayload }) =>
      updatePartnerUser(partnerId, userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_KEY, partnerId] });
    },
  });
};

export const useDeletePartnerUserMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deletePartnerUser(partnerId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_KEY, partnerId] });
    },
  });
};

export const useAddPartnerOrgMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerOrgCreatePayload) => addPartnerOrg(partnerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['data-table', `${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations`],
      });
    },
  });
};

export const useRemovePartnerOrgMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organizationId: string) => removePartnerOrg(partnerId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['data-table', `${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations`],
      });
    },
  });
};

export const usePartnerPortfolioApiKeysQuery = (partnerId: string, enabled = true) => {
  return useQuery({
    queryKey: [PARTNER_API_KEYS_KEY, partnerId],
    queryFn: () => getPartnerPortfolioApiKeys(partnerId),
    enabled: enabled && !!partnerId,
  });
};

export const useMintPartnerPortfolioApiKeyMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => mintPartnerPortfolioApiKey(partnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_KEY, partnerId] });
    },
  });
};

export const useRevokePartnerPortfolioApiKeyMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKeyId: string) => revokePartnerPortfolioApiKey(partnerId, apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_KEY, partnerId] });
    },
  });
};

export const usePartnerEnabledJurisdictionsQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNER_ENABLED_JURISDICTIONS_KEY, partnerId],
    queryFn: () => getPartnerEnabledJurisdictions(partnerId),
    enabled: !!partnerId,
  });
};

export const useUpdatePartnerEnabledJurisdictionsMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerEnabledJurisdictionsUpdate) => updatePartnerEnabledJurisdictions(partnerId, payload),
    onSuccess: (data, variables) => {
      // Write the PUT response immediately so clearing draftOverride does not
      // briefly follow the pre-save cache while a refetch is in flight.
      queryClient.setQueryData([PARTNER_ENABLED_JURISDICTIONS_KEY, partnerId], data);
      // applyToExisting rewrites linked client org country sets; drop their
      // cached reads so Country Settings does not keep showing the pre-apply set.
      if (variables.applyToExisting) {
        queryClient.invalidateQueries({
          predicate: query =>
            query.queryKey[0] === ORGANIZATIONS_STATE_KEY && query.queryKey[2] === 'enabled-jurisdictions',
        });
      }
    },
  });
};

export const usePartnerPackagingQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNER_PACKAGING_KEY, partnerId],
    queryFn: () => getPartnerPackaging(partnerId),
    enabled: !!partnerId,
  });
};

export const useUpdatePartnerPackagingMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerPackagingSettingsUpdate) => updatePartnerPackaging(partnerId, payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData([PARTNER_PACKAGING_KEY, partnerId], data);
      // When the update propagates to linked client orgs, drop their cached
      // packaging reads so an open org page does not keep the pre-apply values.
      if (variables.applyToExisting) {
        queryClient.invalidateQueries({
          predicate: query => query.queryKey[0] === ORGANIZATIONS_STATE_KEY && query.queryKey[2] === 'packaging',
        });
      }
    },
  });
};

export const usePartnerSettingsQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNER_SETTINGS_KEY, partnerId],
    queryFn: () => getPartnerSettings(partnerId),
    enabled: !!partnerId,
  });
};

export const useUpdatePartnerSettingsMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerFeatureSettingsUpdate) => updatePartnerSettings(partnerId, payload),
    onSuccess: (data, payload) => {
      queryClient.setQueryData([PARTNER_SETTINGS_KEY, partnerId], data);
      // Propagated flags land on organization_settings; drop cached org settings
      // so an open client org page does not keep the pre-propagate values.
      if (payload.applyToExisting) {
        queryClient.invalidateQueries({
          predicate: query => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === ORGANIZATIONS_STATE_KEY && key[2] === 'settings';
          },
        });
      }
    },
  });
};

export const useDownloadBillingReportMutation = () => {
  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      downloadBillingReport(startDate, endDate),
    onSuccess: result => {
      const filename = getFilenameFromHeaders(result.headers, 'billing-report');
      downloadBlob(result.data, filename);
      toaster.create({
        title: 'Success',
        description: 'Billing report downloaded successfully',
        type: 'success',
      });
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to download billing report. Please try again.',
        type: 'error',
      });
    },
  });
};

export const usePartnerBrandingQuery = (partnerId: string, enabled = true) => {
  return useQuery({
    queryKey: [PARTNER_BRANDING_KEY, partnerId],
    queryFn: () => getPartnerBranding(partnerId),
    enabled: enabled && !!partnerId,
  });
};

export const useSetPartnerBrandingSubdomainMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerBrandingSubdomainUpdate) => setPartnerBrandingSubdomain(partnerId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PARTNER_BRANDING_KEY, partnerId] }),
  });
};

export const useClearPartnerBrandingSubdomainMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => clearPartnerBrandingSubdomain(partnerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PARTNER_BRANDING_KEY, partnerId] }),
  });
};
